import STATES from '@zenbusiness/graphql-commons-states/static/states';

export const CORP_SUFFIXES = new Set([
  'Co.',
  'Corp.',
  'Corporation',
  'Inc.',
  'Incorporated',
  'Incorporation'
]);
export const LLC_SUFFIXES = new Set(['LLC', 'L.L.C.', 'Limited Liability Company']);

export const ENTITY_TYPE_SUFFIXES = new Set([...CORP_SUFFIXES, ...LLC_SUFFIXES]);
export const ENTITY_TYPES = {
  CORP: { label: 'Corporation', value: 'corp' },
  LLC: { label: 'LLC', value: 'llc' }
};

/**
 * Retrieves the suffix from a business name if it is valid.
 *
 * @param {string} [name=''] - The business name to extract the suffix from.
 * @returns {string|null} The suffix if valid, otherwise null.
 */
export function getBusinessNameSuffix(name = '') {
  const parts = name.trim().toLowerCase().split(' ');

  // Convert all suffixes to lowercase for comparison
  const lowercaseSuffixes = new Set(
    [...ENTITY_TYPE_SUFFIXES].map((suffix) => suffix.toLowerCase())
  );

  // Match against multiple word suffixes
  for (let i = 0; i < parts.length; i += 1) {
    const suffix = parts.slice(i).join(' ');
    if (lowercaseSuffixes.has(suffix)) {
      // Find the original case-sensitive suffix from ENTITY_TYPE_SUFFIXES
      for (const originalSuffix of ENTITY_TYPE_SUFFIXES) {
        if (originalSuffix.toLowerCase() === suffix) {
          return originalSuffix;
        }
      }
    }
  }

  return null;
}

/**
 * Checks if a business name has a valid suffix.
 *
 * @param {string} name - The business name to check for a valid suffix.
 * @returns {boolean} True if the business name ends with a valid suffix, otherwise false.
 */
export function hasBusinessNameSuffix(name) {
  return getBusinessNameSuffix(name);
}

/**
 * Combines a business name and a company indicator into a single string.
 *
 * @param {string} name - The business name to combine.
 * @param {string} companyIndicator - The company indicator to combine.
 * @returns {string} The combined string with the company indicator.
 */
export function getBusinessNameWithIndicator(name, companyIndicator) {
  const nameWithIndicator = companyIndicator ? `${name} ${companyIndicator}` : name;
  return nameWithIndicator;
}

/**
 * Retrieves the entity type label from a given value.
 *
 * @param {string} value - The entity type value to find the label for.
 * @returns {string|null} The label of the entity type if found, otherwise null.
 */
export function getEntityTypeLabelFromValue(value) {
  for (const key of Object.keys(ENTITY_TYPES)) {
    if (ENTITY_TYPES[key].value === value) {
      return ENTITY_TYPES[key].label;
    }
  }
  return null;
}

/**
 * Retrieves the entity type value from a given label.
 *
 * @param {string} label - The entity type label to find the value for.
 * @returns {string|null} The value of the entity type if found, otherwise null.
 */
export function getEntityTypeValueFromLabel(label) {
  for (const key of Object.keys(ENTITY_TYPES)) {
    if (ENTITY_TYPES[key].label === label) {
      return ENTITY_TYPES[key].value;
    }
  }
  return null;
}

/**
 * Retrieves the jurisdiction state label from a given value.
 *
 * @param {string} value - The value of the jurisdiction state.
 * @param {Array<object>} [states=STATES] - The array of jurisdiction states.
 * @returns {string|null} The label of the jurisdiction state if found, otherwise null.
 */
export function getJurisdictionStateLabelFromValue(value, states = STATES) {
  const label = states.find(({ value: stateValue }) => stateValue === value)?.label;
  return label || null;
}
